'use client'

import {
  getTrackingCartItemPurchaseMode,
  getTrackingPromotionLabel,
} from '@/feature/cart/services/cart.tracking'
import { PRODUCT_IMAGE_SIZE_THUMBNAIL } from '@/feature/product/services/product.constants'
import { QuantityPicker } from '@/ui/form/inputs/quantity-picker/QuantityPicker.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Link } from '@/ui/links/link/Link.client'
import { CloudflarePictureProduct } from '@/ui/media/cloudflare-picture-product/CloudflarePictureProduct.client'
import { gql } from '@gql-generated/gql'
import type { ProductRowReadOnly_ProductFragment } from '@gql-generated/graphql'
import { toProductPage } from '../../services/product.routing'
import { ProductPrice } from '../product-price/ProductPrice.client'

gql(/* GraphQL */ `
  fragment ProductRowReadOnly_Product on Product {
    urlKey
    reference
    parentReference
    type
    name
    brand {
      name
      urlKey
    }
    analytics {
      promotionStatus
      profileCase
    }
    bestArticlePrice
    mainImage {
      ...FullPicture
    }
    stock {
      availability
      isSellable
      packaging {
        ...FullPackaging
      }
    }
    nomenclatureFamily
    nomenclatureGroup
    canAddToCart
    clickAndCollectStatus
  }

  fragment ProductRowReadOnly_CartItem on CartItem {
    id
    quantity
    analyticsPromotionStatus
    totalExclTax
    unitPrice {
      exclTax
    }
    discount {
      percentage
      catalogDiscountAmount
    }
    product {
      ...ProductRowReadOnly_Product
    }
  }
`)

export type ProductRowReadOnlyItem = {
  quantity?: number | null
  analyticsPromotionStatus?: string | null
  totalExclTax?: number | null
  unitPrice?: { exclTax?: number | null } | null
  discount?: { percentage?: number | null; catalogDiscountAmount?: number | null } | null
  product?: ProductRowReadOnly_ProductFragment | null
}

export type ProductRowReadOnlyProps = {
  cartId?: string

  /** The product displayed */

  cartItem: ProductRowReadOnlyItem

  position?: number
}

export function ProductRowReadOnly({ cartItem, position }: ProductRowReadOnlyProps) {
  const { t } = useTranslation(['generic', 'productRow'])
  const { product } = cartItem
  const isAuthenticated = useIsAuthenticated()

  if (!product) {
    console.error('ProductRowReadOnly: product is missing', cartItem)

    return null
  }

  const packagingQuantity = product?.stock?.packaging?.quantity ?? 1
  const unitPriceExclTax = cartItem.unitPrice?.exclTax ?? 0
  const totalPriceExclTax = cartItem.totalExclTax ?? 0

  return (
    <div className="flex flex-row gap-3" data-testid="cart-item">
      {/* Image */}
      <div className="shrink-0" data-testid="cart-item/picture">
        <Link variant="free" href={toProductPage(product.urlKey)}>
          <CloudflarePictureProduct
            className="size-[3.375rem] desktop:size-9"
            src={product.mainImage?.set?.[0]?.url}
            alt={product.mainImage?.alt || product.name || ''}
            width={PRODUCT_IMAGE_SIZE_THUMBNAIL}
          />
        </Link>
      </div>
      {/* Content */}
      <div className="flex flex-1 flex-col gap-2 tablet:flex-row">
        <div className="flex grow basis-0 flex-col flex-wrap gap-2 tablet:flex-row desktop:flex-row desktop:gap-3">
          <div className="flex-1 tablet:min-w-[12rem] tablet:max-w-[21.25rem]">
            <span className="inline-flex flex-col gap-1">
              {product.brand && (
                <span
                  className="font-bold uppercase leading-light text-brand-2-900"
                  data-testid="product-card/brand"
                >
                  {product.brand.name}
                </span>
              )}
              <Link
                variant="free"
                underlined="hover"
                href={toProductPage(product.urlKey)}
                trackingData={{
                  event: 'select_item',
                  ecommerce: {
                    currency: 'EUR',
                    items: [
                      {
                        item_id: product.reference,
                        item_code_p: product.parentReference || '',
                        item_nature: product?.type?.toLowerCase() || 'article',
                        item_name: product.name || '',
                        item_price:
                          ('bestArticlePrice' in product ? product.bestArticlePrice : null) ||
                          cartItem.totalExclTax ||
                          0,
                        item_index: position?.toString() || '',
                        item_brand: product.brand?.name || '',
                        item_category: product.nomenclatureFamily || '',
                        item_category2: product.nomenclatureGroup || '',
                        item_list_name: 'page panier',
                        item_promotion: getTrackingPromotionLabel(
                          product.analytics?.promotionStatus,
                          cartItem.analyticsPromotionStatus,
                        ),
                        item_public_price: isAuthenticated ? 'true' : 'false',
                        item_purchase_mode: getTrackingCartItemPurchaseMode(
                          product?.stock?.availability || [],
                          product?.stock?.isSellable ?? product?.canAddToCart ?? false,
                          isAuthenticated,
                        ),
                        item_stock_info: product.analytics?.profileCase || '',
                      },
                    ],
                  },
                }}
              >
                <span
                  className="line-clamp-2 text-left text-lg leading-light"
                  data-testid="product-card/product-name"
                >
                  {product.name}
                </span>
              </Link>
              <span className="text-sm text-brand-2-900">
                {t('productRow:ref', { ref: product.reference })}
              </span>
            </span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-start gap-1 leading-light tablet:w-18 tablet:items-end">
          {/* Prices */}
          <div className="flex flex-col items-start tablet:items-end" data-testid="cart-item/price">
            <ProductPrice
              price={totalPriceExclTax}
              unitPrice={unitPriceExclTax}
              packaging={{ ...product.stock?.packaging, quantity: packagingQuantity }}
              discount={{
                price: cartItem.discount?.catalogDiscountAmount || 0,
                percentage: cartItem.discount?.percentage || 0,
              }}
              size="2xl"
              className="tablet:justify-end tablet:justify-items-end tablet:text-right"
              wrapperClassName="flex-col tablet:items-end"
            />
          </div>
          {cartItem.quantity != null && <QuantityPicker isReadOnly value={cartItem.quantity} />}
        </div>
      </div>
    </div>
  )
}
